import { post } from "~/hooks/use-api";
import type { OrgConflict } from "~/api/priority-message/conflict";
// Mirrors the caps documented on createMessage/editMessage in
// gen/rpc/priority-messages.ts
export const HEADER_MAX_LENGTH = 100;
export const BODY_MAX_LENGTH = 360;

/**
 * Counts a field value the way the api does, so the counter shown while typing
 * and the validation on submit can never disagree: trimmed, and by code point
 * so an emoji counts once.
 */
export function countCharacters(value: string): number {
  return [...value.trim()].length;
}

export const DEFAULT_HEADER = "Important Notice";

/**
 * Operator only ever creates and assigns "operator" source messages - the
 * "office" source is owned by the org's own staff and is assigned per location.
 */
export const OPERATOR_SOURCE = "operator";

export type MessageFlag = "cart-always" | "pinned";

export const MESSAGE_FLAGS: {
  value: MessageFlag;
  label: string;
  description: string;
}[] = [
  {
    value: "cart-always",
    label: "Always show in Cart",
    description:
      "Interrupts checkout - every customer who opens their Cart has to dismiss this before they can keep ordering.",
  },
  {
    value: "pinned",
    label: "Pinned",
    description:
      "When the message is dismissed, it will move to the top of the screen to be reopened",
  },
];

const FLAG_VALUES = MESSAGE_FLAGS.map((flag) => flag.value);

/**
 * flags is an array of strings on the message. Unrecognised flags are ignored,
 * and dropped if the message is saved from here.
 */
export function parseFlags(flags: string[] | null | undefined): MessageFlag[] {
  return (flags ?? []).filter((flag): flag is MessageFlag =>
    (FLAG_VALUES as string[]).includes(flag)
  );
}

export function serializeFlags(flags: MessageFlag[]): MessageFlag[] {
  return FLAG_VALUES.filter((flag) => flags.includes(flag));
}

export function flagLabels(flags: string[] | null | undefined): string[] {
  return parseFlags(flags).map(
    (flag) =>
      MESSAGE_FLAGS.find((f) => f.value === flag)?.label ?? (flag as string)
  );
}

/**
 * An org shows one operator message at a time, so assigning a second one comes
 * back as a 409 the user can choose to resolve by replacing.
 */
export function isAssignConflict(err: unknown): boolean {
  const { status, code } = (err ?? {}) as { status?: number; code?: string };

  return status === 409 || code === "org_message_conflict";
}

/**
 * The orgs that would block assigning to `orgIds`, checked before anything is
 * created or changed. An empty list means the assignment can go ahead.
 */
export async function checkOrgConflicts(
  orgIds: string[],
  messageId?: string
): Promise<OrgConflict[]> {
  if (!orgIds.length) return [];

  return post<{ orgIds: string[]; messageId?: string }, OrgConflict[]>(
    "/api/priority-message/org-conflicts",
    { orgIds, messageId }
  );
}

export function confirmReplace(detail: string): boolean {
  return window.confirm(
    `${detail}\n\nReplace it? The message it has now stays assigned but stops showing.`
  );
}
